/**
 * Murphy Door Configurator - Selection State Coordinator
 *
 * PURPOSE: Single source of truth for ALL BROWSING state transitions.
 * Eliminates code duplication by providing one unified entry point
 * for entering SELECTION_BROWSING state.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handler for bootstrap (Pattern 4)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first O(1) data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor (Pattern 9)
 * - UI updates through conductor delegation (Pattern 10)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - No user input rendered to DOM (Pattern 17 - N/A for pure BL)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - Conductor single publication (Pattern 20)
 *
 * CONSUMERS:
 * - md-config-core-event-handler.js (single-variant, multi-variant selections)
 * - md-config-bl-grouped-product-handler.js (CompositeProduct auto-selections)
 *
 * PERFORMANCE CHARACTERISTICS:
 * - Variant ID resolution: O(1) PPD Map lookup
 * - Composite context detection: O(1) PPD Map lookup
 * - Expected execution time: ~1-10ms
 *
 * All external communication flows through md-config-core-conductor.js delegation.
 *
 * @version 2.0.0
 * @author Murphy Door Configurator Team
 */

(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ALL events use CONSTANTS.EVENTS.* - NO local EVENT_TYPES objects (DRY)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    BROWSING_COORDINATION: 100, // ms - full coordination cycle
  });

  const ERROR_MESSAGES = Object.freeze({
    CONTEXT_REQUIRED: 'context is required',
    PRODUCT_ID_REQUIRED: 'productId is required',
    CATEGORY_ID_REQUIRED: 'categoryId is required',
    ELEMENT_REQUIRED: 'element is required',
    CONDUCTOR_REQUIRED: 'conductor is required',
    CONDUCTOR_STATE_CHANGE_UNAVAILABLE:
      'Conductor handleStateChange method not available',
    NOT_BOOTSTRAPPED: 'SelectionCoordinator called before bootstrap complete',
    PPD_UNAVAILABLE: 'PPD not available via conductor',
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[SelectionCoordinator] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(
          `[SelectionCoordinator] Error publishing ${eventType}:`,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // ============================================================================
  if (CoreEventBus) {
    // Subscribe to bootstrap completion at module load time (race protection)
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log(
            '[SelectionCoordinator] Bootstrap complete event received'
          );
        bootstrapComplete = true;
        isInitialized = true;

        // Process all queued calls
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[SelectionCoordinator] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn
                .apply(call.context, call.args)
                .then(call.resolve)
                .catch(call.reject);
            } catch (error) {
              isDev() &&
                console.error(
                  '[SelectionCoordinator] Synchronous error processing queued call',
                  error
                );
              call.reject(error);
            }
          });
          pendingCalls = [];
        }

        // Publish module ready event
        publishEvent(EVENTS.MODULE_SELECTION_COORDINATOR_READY, {
          module: 'selectionCoordinator',
          timestamp: Date.now(),
        });
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    isDev() &&
      console.log(
        '[SelectionCoordinator] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // CLEANUP FUNCTION (Pattern 15)
  // ============================================================================
  function cleanup() {
    isDev() && console.log('[SelectionCoordinator] Cleaning up resources');

    cleanupHandlers.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (e) {
        /* already cleaned */
      }
    });
    cleanupHandlers.length = 0;

    pendingCalls = [];
    isInitialized = false;
    bootstrapComplete = false;

    publishEvent(EVENTS.MODULE_SELECTION_COORDINATOR_CLEANUP, {
      module: 'selectionCoordinator',
      timestamp: Date.now(),
    });
  }

  // ============================================================================
  // MODULE IMPLEMENTATION
  // ============================================================================
  window.MurphyDoorConfigurator.selectionCoordinator = {
    /**
     * UNIFIED BROWSING STATE ENTRY POINT (Wrapper)
     * Handles queuing for bootstrap barrier.
     *
     * @param {Object} context - Selection context from caller
     * @param {string} context.productId - REQUIRED: Product ID
     * @param {string} context.categoryId - REQUIRED: Category ID
     * @param {HTMLElement} context.element - REQUIRED: DOM element reference
     * @param {string|null} context.variantId - Variant ID (null for single-variant, coordinator will resolve)
     * @param {boolean} context.autoApplied - Was this auto-applied by business logic?
     * @param {Object} context.compositeContext - Composite context (optional)
     * @param {number} context.materialScore - Material preference score (optional)
     * @param {boolean} context.isOneStep - Single-step auto-apply flag (optional)
     * @param {Object} conductor - REQUIRED: Conductor for delegation
     *
     * @returns {Promise<Object>} { success: boolean, payload?: Object, error?: string }
     */
    enterBrowsingState: function (context, conductor) {
      // PATTERN 6: Bootstrap runtime protection - queue if not ready
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            '[SelectionCoordinator] Bootstrap not complete, queuing call'
          );
        return new Promise((resolve, reject) => {
          pendingCalls.push({
            fn: this._executeBrowsingState,
            context: this,
            args: [context, conductor],
            resolve,
            reject,
          });
        });
      }
      return this._executeBrowsingState(context, conductor);
    },

    /**
     * Core async logic for entering browsing state.
     *
     * Implements 5-stage event lifecycle:
     * STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED
     *
     * @private
     */
    _executeBrowsingState: async function (context, conductor) {
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();
      const METHOD_SIGNATURE = 'SelectionCoordinator.enterBrowsingState';

      try {
        // ================================================================
        // STAGE 1: START - Announce operation beginning
        // ================================================================
        publishEvent(EVENTS.BUSINESS_BROWSING_STATE_COORDINATION_STARTED, {
          context: {
            productId: context?.productId,
            categoryId: context?.categoryId,
          },
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // ================================================================
        // STAGE 2: VALIDATION - Validate context (fail-fast)
        // ================================================================
        publishEvent(EVENTS.BUSINESS_BROWSING_CONTEXT_VALIDATION_STARTED, {
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        const validationResult = this._validateContext(context, conductor);
        if (!validationResult.valid) {
          publishEvent(EVENTS.BUSINESS_BROWSING_CONTEXT_VALIDATION_FAILED, {
            errors: validationResult.errors,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          throw new Error(
            `Context validation failed: ${validationResult.errors.join(', ')}`
          );
        }

        publishEvent(EVENTS.BUSINESS_BROWSING_CONTEXT_VALIDATION_COMPLETED, {
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // ================================================================
        // STAGE 3: PROGRESS - Detect product type, resolve variant, build payload
        // ================================================================
        const productType = this._detectProductType(context);

        publishEvent(EVENTS.BUSINESS_BROWSING_PRODUCT_TYPE_DETECTED, {
          productType,
          productId: context.productId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // PATTERN 7 & 9: PPD-First + Data via conductor delegation
        let resolvedVariantId = context.variantId;

        if (!resolvedVariantId && productType === 'single-variant') {
          resolvedVariantId = this._resolveVariantId(
            context.productId,
            conductor
          );

          if (!resolvedVariantId) {
            isDev() &&
              console.warn(
                `[SelectionCoordinator] Could not resolve variantId for single-variant product ${context.productId}`
              );
          }
        }

        // Detect composite context via PPD
        const compositeContext = this._detectCompositeContext(
          context,
          conductor
        );

        if (compositeContext) {
          publishEvent(EVENTS.BUSINESS_BROWSING_COMPOSITE_CONTEXT_RESOLVED, {
            compositeContext,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
        }

        // Build standardized payload
        const standardPayload = this._buildStandardPayload({
          ...context,
          variantId: resolvedVariantId,
          productType,
          compositeContext: compositeContext || context.compositeContext,
        });

        publishEvent(
          EVENTS.BUSINESS_BROWSING_PAYLOAD_STANDARDIZATION_COMPLETED,
          {
            payload: standardPayload,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          }
        );

        // ================================================================
        // STAGE 4 (SUCCESS PATH): Delegate to conductor
        // PATTERN 20: Conductor Single Publication
        // ================================================================
        if (!conductor || typeof conductor.handleStateChange !== 'function') {
          throw new Error(ERROR_MESSAGES.CONDUCTOR_STATE_CHANGE_UNAVAILABLE);
        }

        conductor.handleStateChange('SELECTION_BROWSING', standardPayload);

        publishEvent(EVENTS.BUSINESS_BROWSING_STATE_DELEGATION_COMPLETED, {
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // HYBRID PATTERN: SELECTION_BROWSING event publication REMOVED from here.
        // Conductor is the SINGLE SOURCE OF TRUTH for SELECTION_BROWSING publication.
        // The conductor publishes the typed SELECTION_BROWSING event after completing
        // its full orchestration (validation, auto-toggle, state handling, UI updates).

        // ================================================================
        // PATTERN 12: PERFORMANCE TRACKING (10 points)
        // ================================================================
        const endTime = window.performance
          ? window.performance.now()
          : Date.now();
        const executionTime = endTime - startTime;

        if (executionTime > PERFORMANCE_THRESHOLDS.BROWSING_COORDINATION) {
          publishEvent(EVENTS.PERFORMANCE_BROWSING_COORDINATION_SLOW, {
            executionTime: executionTime.toFixed(2),
            threshold: PERFORMANCE_THRESHOLDS.BROWSING_COORDINATION,
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });

          isDev() &&
            console.warn(
              `[SelectionCoordinator] PERFORMANCE: ${METHOD_SIGNATURE} took ${executionTime.toFixed(2)}ms ` +
                `(threshold: ${PERFORMANCE_THRESHOLDS.BROWSING_COORDINATION}ms)`
            );
        }

        // ================================================================
        // STAGE 4: COMPLETED - Publish coordination success
        // ================================================================
        publishEvent(EVENTS.BUSINESS_BROWSING_STATE_COORDINATION_COMPLETED, {
          payload: standardPayload,
          executionTime: executionTime.toFixed(2),
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        isDev() &&
          console.log(
            `[SelectionCoordinator] BROWSING state coordination completed in ${executionTime.toFixed(2)}ms`,
            standardPayload
          );

        return { success: true, payload: standardPayload };
      } catch (error) {
        // ================================================================
        // STAGE 5: FAILED - Publish operation failure
        // PATTERN 14: Try/Catch + Failed Events (10 points)
        // ================================================================
        const duration =
          (window.performance ? window.performance.now() : Date.now()) -
          startTime;

        publishEvent(EVENTS.BUSINESS_BROWSING_STATE_COORDINATION_FAILED, {
          error: error.message,
          context: {
            productId: context?.productId,
            categoryId: context?.categoryId,
          },
          duration: duration.toFixed(2),
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        isDev() &&
          console.error(
            `[SelectionCoordinator] Coordination failed - ${error.message}`,
            error
          );
        return { success: false, error: error.message };
      }
    },

    /**
     * Validate context has all required fields
     * @private
     */
    _validateContext: function (context, conductor) {
      const errors = [];

      if (!context) {
        errors.push(ERROR_MESSAGES.CONTEXT_REQUIRED);
        return { valid: false, errors };
      }

      if (!context.productId) {
        errors.push(ERROR_MESSAGES.PRODUCT_ID_REQUIRED);
      }

      if (!context.categoryId) {
        errors.push(ERROR_MESSAGES.CATEGORY_ID_REQUIRED);
      }

      if (!context.element) {
        errors.push(ERROR_MESSAGES.ELEMENT_REQUIRED);
      }

      if (!conductor) {
        errors.push(ERROR_MESSAGES.CONDUCTOR_REQUIRED);
      }

      return {
        valid: errors.length === 0,
        errors,
      };
    },

    /**
     * Detect product type based on context
     * @private
     */
    _detectProductType: function (context) {
      // Check for composite product indicators
      if (
        context.compositeContext?.isCompositeProduct ||
        context.element?.getAttribute('data-is-composite') === 'true'
      ) {
        return 'composite';
      }

      // Check if variantId is provided
      if (context.variantId) {
        return 'multi-variant';
      }

      // Default to single-variant
      return 'single-variant';
    },

    /**
     * Resolve variantId for single-variant products using PPD via conductor delegation
     * PATTERN 7: Perfect Dictionary Only (10 points)
     * PATTERN 8: No Raw Data Access (10 points)
     * @private
     */
    _resolveVariantId: function (productId, conductor) {
      if (
        !conductor ||
        typeof conductor.getPerfectProductDictionary !== 'function'
      ) {
        isDev() &&
          console.warn(
            '[SelectionCoordinator] Conductor getPerfectProductDictionary not available'
          );
        return null;
      }

      // PATTERN 9: Data access through conductor delegation
      const ppd = conductor.getPerfectProductDictionary();

      if (!ppd || !ppd.productIdToVariantIdMap) {
        isDev() &&
          console.warn(
            '[SelectionCoordinator] PPD productIdToVariantIdMap not available'
          );
        return null;
      }

      const variantId = ppd.productIdToVariantIdMap.get(productId);

      if (variantId) {
        isDev() &&
          console.log(
            `[SelectionCoordinator] Resolved variantId for single-variant product: ${variantId}`
          );
      }

      return variantId || null;
    },

    /**
     * Detect composite context via hierarchy: explicit flags -> PPD -> DOM (fallback)
     * PATTERN 7: PPD-first O(1) data access
     * @private
     */
    _detectCompositeContext: function (context, conductor) {
      // Priority 1: Explicit flag in context
      if (context.compositeContext?.isCompositeProduct) {
        return context.compositeContext;
      }

      // Priority 2: PPD lookup (Source of Truth) via conductor delegation
      if (
        conductor &&
        typeof conductor.getPerfectProductDictionary === 'function'
      ) {
        const ppd = conductor.getPerfectProductDictionary();

        if (ppd?.compositeGroupIdToProductMap?.has(context.productId)) {
          const compositeProduct = ppd.compositeGroupIdToProductMap.get(
            context.productId
          );

          if (compositeProduct) {
            return {
              isCompositeProduct: true,
              groupId: compositeProduct.groupId || context.productId,
              groupName:
                compositeProduct.groupName || compositeProduct.displayName,
              categoryId: context.categoryId,
              element: context.element,
            };
          }
        }
      }

      // Priority 3: DOM attributes (Fallback)
      const element = context.element;
      if (element?.getAttribute('data-is-composite') === 'true') {
        const groupId = element.getAttribute('data-group-id');
        const groupName = element.getAttribute('data-group-name');

        if (groupId && groupName) {
          isDev() &&
            console.warn(
              '[SelectionCoordinator] Falling back to DOM attributes for composite context. PPD lookup should be the primary source.'
            );
          return {
            isCompositeProduct: true,
            groupId,
            groupName,
            categoryId: context.categoryId,
            element: element,
          };
        }
      }

      return null;
    },

    /**
     * Build standardized payload with all metadata
     * @private
     */
    _buildStandardPayload: function (context) {
      const payload = {
        // Core (always present)
        productId: context.productId,
        categoryId: context.categoryId,
        element: context.element,
        timestamp: window.performance ? window.performance.now() : Date.now(),

        // Product type (conditional)
        variantId: context.variantId || null,
        productType: context.productType,

        // Metadata (always present)
        autoApplied: context.autoApplied || false,
        processedBy: 'SelectionCoordinator.enterBrowsingState',

        // UX flags (conditional)
        ...(context.isOneStep !== undefined && {
          isOneStep: context.isOneStep,
        }),
        ...(context.requiresApplyButton !== undefined && {
          requiresApplyButton: context.requiresApplyButton,
        }),
        // C1: opt a browsing entry out of the click=apply chokepoint (used by
        // the slider's initial render so the untouched default is never
        // auto-applied — sliders commit on release).
        ...(context.suppressClickApply !== undefined && {
          suppressClickApply: context.suppressClickApply,
        }),

        // Composite context (conditional)
        ...(context.compositeContext && {
          isCompositeProduct: true,
          groupId: context.compositeContext.groupId,
          groupName: context.compositeContext.groupName,
          compositeContext: context.compositeContext,
        }),

        // Material preference (conditional)
        ...(context.materialScore !== undefined && {
          materialScore: context.materialScore,
        }),
      };

      // NOTE: Object.freeze() NOT applied here - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
      return payload;
    },

    /**
     * PATTERN 5: Event Replay Ready (10 points)
     * Returns module state for replay/diagnostics
     */
    getModuleState: function () {
      return {
        isInitialized,
        bootstrapComplete,
        pendingCallsCount: pendingCalls.length,
        module: 'selectionCoordinator',
      };
    },

    /**
     * Cleanup all resources (Pattern 15)
     */
    cleanup: cleanup,
  };

  isDev() && console.log('[SelectionCoordinator] Module loaded');
})(window);
